/**
 * Service de Synchronisation Intelligente des Catalogues Fournisseurs
 * Date: 29/10/2025
 * 
 * Gère la détection des modifications et la synchronisation automatique
 * des catalogues fournisseurs vers les catalogues utilisateurs
 */

import { apiFetch } from './httpClient';

// ============================================================================
// INTERFACES
// ============================================================================

export interface CatalogueVersion {
  fournisseur: string;
  version: number;
  hash: string;
  nb_produits: number;
  nb_produits_visibles: number;
  derniere_modification: string;
  modifie_par: string;
}

export interface UpdatesInfo {
  has_updates: boolean;
  current_version: number;
  last_sync_version: number;
  nb_versions_pending: number;
  nb_produits_visibles: number;
  derniere_modification: string;
}

export interface Modification {
  version: number;
  type: string;
  date: string;
  details: any;
  nb_produits_visibles: number;
}

export interface Changelog {
  from_version: number;
  to_version: number;
  nb_modifications: number;
  modifications: Modification[];
}

export interface SyncConfig {
  sync_auto_active: boolean;
  sync_categories: string[];
  remise_appliquee: number;
  derniere_sync_version?: number;
  derniere_sync_date?: string;
}

export interface SyncResult {
  message: string;
  nb_imports: number;
  nb_updates: number;
  nb_ignores: number;
  version: number;
}

// ============================================================================
// SERVICE
// ============================================================================

class CatalogueSyncService {
  /**
   * Vérifie s'il y a des mises à jour disponibles pour un catalogue
   */
  async checkUpdates(fournisseur: string, userId: number): Promise<UpdatesInfo> {
    return apiFetch(`/catalogues/${encodeURIComponent(fournisseur)}/check-updates?user_id=${userId}`);
  }

  /**
   * Récupère le changelog entre deux versions
   */
  async getChangelog(
    fournisseur: string,
    fromVersion?: number,
    toVersion?: number
  ): Promise<Changelog> {
    const params: any = {};
    if (fromVersion !== undefined) params.from_version = fromVersion;
    if (toVersion !== undefined) params.to_version = toVersion;

    const query = new URLSearchParams(params).toString();
    return apiFetch(
      `/catalogues/${encodeURIComponent(fournisseur)}/changelog${query ? `?${query}` : ''}`
    );
  }

  /**
   * Récupère la configuration de synchronisation
   */
  async getSyncConfig(userId: number, fournisseur: string): Promise<SyncConfig> {
    return apiFetch(
      `/users/${userId}/fournisseurs/${encodeURIComponent(fournisseur)}/sync-config`
    );
  }

  /**
   * Met à jour la configuration de synchronisation
   */
  async updateSyncConfig(
    userId: number,
    fournisseur: string,
    config: Partial<SyncConfig>
  ): Promise<{ message: string }> {
    return apiFetch(
      `/users/${userId}/fournisseurs/${encodeURIComponent(fournisseur)}/sync-config`,
      {
        method: 'POST',
        body: JSON.stringify(config),
      }
    );
  }

  /**
   * Synchronise le catalogue fournisseur
   */
  async synchroniser(
    userId: number,
    fournisseur: string,
    options: {
      tenant_id: number;
      categories?: string[];
      remise_appliquee?: number;
    }
  ): Promise<SyncResult> {
    return apiFetch(
      `/users/${userId}/fournisseurs/${encodeURIComponent(fournisseur)}/synchroniser`,
      {
        method: 'POST',
        body: JSON.stringify(options),
      }
    );
  }

  /**
   * Vérifie les mises à jour pour tous les fournisseurs d'un utilisateur
   */
  async checkAllUpdates(userId: number, fournisseurs: string[]): Promise<Map<string, UpdatesInfo>> {
    const results = new Map<string, UpdatesInfo>();
    
    await Promise.all(
      fournisseurs.map(async (fournisseur) => {
        try {
          const updates = await this.checkUpdates(fournisseur, userId);
          results.set(fournisseur, updates);
        } catch (error) {
          console.error(`Erreur check updates ${fournisseur}:`, error);
        }
      })
    );

    return results;
  }

  /**
   * Formatte le résumé des modifications
   */
  formatChangelog(changelog: Changelog): string {
    if (changelog.nb_modifications === 0) {
      return 'Aucune modification';
    }

    const summary: string[] = [];
    const types = new Map<string, number>();

    changelog.modifications.forEach((modif) => {
      const count = types.get(modif.type) || 0;
      types.set(modif.type, count + 1);
    });

    types.forEach((count, type) => {
      const label = this.getLabelType(type);
      summary.push(`${count} ${label}`);
    });

    return summary.join(', ');
  }

  /**
   * Retourne le label français pour un type de modification
   */
  private getLabelType(type: string): string {
    const labels: { [key: string]: string } = {
      ajout: 'ajout(s)',
      mise_a_jour: 'mise(s) à jour',
      suppression: 'suppression(s)',
      initial: 'initialisation',
    };
    return labels[type] || type;
  }

  /**
   * Calcule le délai depuis la dernière sync
   */
  getDelaiDepuisSync(derniereSyncDate?: string): string {
    if (!derniereSyncDate) return 'Jamais synchronisé';

    const now = new Date();
    const syncDate = new Date(derniereSyncDate);
    const diffMs = now.getTime() - syncDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 60) {
      return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    }

    const diffHeures = Math.floor(diffMinutes / 60);
    if (diffHeures < 24) {
      return `Il y a ${diffHeures} heure${diffHeures > 1 ? 's' : ''}`;
    }

    const diffJours = Math.floor(diffHeures / 24);
    return `Il y a ${diffJours} jour${diffJours > 1 ? 's' : ''}`;
  }
}

export default new CatalogueSyncService();
